(ns machtfit.startpage.maps
  (:require ["ol" :as ol]
            ["ol/Feature$default" :as Feature]
            ["ol/geom/Point$default" :as Point]
            ["ol/interaction/Select.js$default" :as SelectInteraction]
            ["ol/layer/Tile$default" :as TileLayer]
            ["ol/layer/Vector$default" :as VectorLayer]
            ["ol/proj$fromLonLat" :as fromLonLat]
            ["ol/source/OSM$default" :as OSM]
            ["ol/source/Vector$default" :as VectorSource]
            ["ol/style/Icon$default" :as Icon]
            ["ol/style/Style$default" :as Style]
            [goog.object]
            [machtfit.app.tracking :as tracking]
            [machtfit.core :as machtfit]
            [machtfit.responsive :as responsive]
            [machtfit.startpage.subs :as subs]
            [machtfit.translation :refer [t]]
            [machtfit.utils :as u]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [vimsical.re-frame.cofx.inject :as inject]))

(def osm-layer
  (let [osm-attribution "© <a target='_blank' href='https://www.openstreetmap.org/copyright'>OpenStreetMap contributors</a>"
        here-attribution "© <a target='_blank' href='https://www.here.com'>HERE</a>"]
    (TileLayer.
     (clj->js
      {:source (OSM.
                (clj->js {:url "https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                          :attributions [osm-attribution
                                         here-attribution]
                          :tileLoadFunction (fn [tile src]
                                              (let [img (.getImage tile)]
                                                (aset img "referrerPolicy" "strict-origin-when-cross-origin")
                                                (set! (.-src img) src)))}))}))))

(defn vector-layer [features style]
  (VectorLayer.
   (clj->js {:source (VectorSource. (clj->js {:features features}))
             :style style})))

(defn coords->js [coords]
  (fromLonLat (clj->js coords)))

(defn point [coords]
  (Feature. (clj->js {:geometry (Point. (coords->js coords))})))

(defn product-point [coords]
  (doto (point coords)
    (.setProperties (clj->js {:coords coords}))))

(defn style [m]
  (Style. (clj->js m)))

(defn icon [src]
  (Icon. (clj->js {:src src})))

(def active-marker-style
  (style {:image (icon (machtfit/static "map/marker.png"))}))

(defn make-layers [coords]
  (let [products-points (mapv product-point coords)
        productLayer (vector-layer products-points
                                   active-marker-style)]
    (clj->js
     [osm-layer
      productLayer])))

(defn product-list-for-location []
  (let [coords @(rf/subscribe [:get [:ui :map-properties :clicked-marker-coords]])
        products @(rf/subscribe [::subs/products-at-coordinates coords])
        show? coords]
    [:div.map-product-overlap
     {:class (when show? "active")}
     [:button.close
      {:on-click #(rf/dispatch [::set-clicked-marker-coords nil])}]

     (when coords
       [:div.map-product-list
        (for [{:keys [category title supplier address image-url image-2x-url
                      raw-product-id id]} products
              :let [{:keys [name street postcode city]} address
                    product-id (or raw-product-id id)]]
          [:div.map-product-list-item
           {:key id}
           [:a
            {:href (str "/angebote/" product-id "/")}
            [:div.map-popup__product-thumbnail
             [:picture
              [:source {:src-set (str image-url " 1x, " image-2x-url " 2x")}]
              [:img {:src image-url :style {:height "100%" :width "100%" :object-fit "cover"}}]]]
            [:div.map-popup__content
             [:h6.map-popup__category category]
             [:h5.map-popup__title title]
             [:span.map-popup__partner (:name supplier)]
             [:div.map-popup__ {:keys [name street postcode city]}
              (into [:span]
                    (interpose
                     [:br]
                     (remove empty?
                             [name street (str postcode " " city)])))]]]])])]))

(defn- no-results-popup []
  (let [product-types @(rf/subscribe [::subs/effective-filter :product-types])
        products-for-map? @(rf/subscribe [::subs/has-products-for-map?])
        products-loaded? @(rf/subscribe [::subs/products-loaded?])

        [show-button? message]
        (cond
          (not products-loaded?)
          [false
           {:de "Angebote in Ihrer Nähe werden gesucht..."
            :en "Looking for offers close to you..."}]

          (= product-types #{"online"})
          [true
           {:de "In der Kartenansicht können keine Online Angebote angezeigt werden."
            :en "The map view can’t show online offers."}]

          (and product-types
               (not (contains? product-types "with-location")))
          [true
           {:de "In der Kartenansicht können keine Produkte angezeigt werden."
            :en "The map view can’t show products."}]

          (not products-for-map?)
          [false
           {:de "Es gibt keine Angebote am von Ihnen gewählten Ort."
            :en "There are no onsite offers in your selected location."}]

          :else nil)]

    (when message
      [:div.map-popup
       [:p (t message)]
       (when show-button?
         [:button
          {:on-click #(do (machtfit/stop-event %)
                          (rf/dispatch [:machtfit.startpage.filterbar/toggle-product-types "with-location"]))}
          (t {:de "Angebote vor Ort anzeigen"
              :en "Show onsite offers"})])
       [:a {:href "#"
            :on-click #(do
                         (machtfit/stop-event %)
                         (rf/dispatch [:machtfit.startpage.filterbar/map-toggle-changed "map__no-results-popup__back-to-list"]))}

        (t {:de "zurück zur Listenansicht"
            :en "back to list view"})]])))

(rf/reg-event-fx ::product-marker-clicked
                 ;; this injection triggers a warning about
                 ;; subscribe being called outside of a reactive context
  [(rf/inject-cofx ::inject/sub
                   (fn [[_ coords]]
                     [::subs/products-at-coordinates coords]))]
  (fn [{:keys [db ::subs/products-at-coordinates]} [_ [longitude latitude]]]
    {::tracking/capture-event [db
                               "clicked_marker_on_map"
                               {:lat latitude
                                :long longitude
                                :product-ids (map #(or (:raw-product-id %) (:id %))
                                                  products-at-coordinates)}]}))

(rf/reg-event-db ::set-map-properties
  (fn [db [_ new-map-properties]]
    (assoc-in db [:ui :map-properties] new-map-properties)))

(rf/reg-event-db ::set-clicked-marker-coords
  (fn [db [_ clicked-marker-coords]]
    (assoc-in db [:ui :map-properties :clicked-marker-coords] clicked-marker-coords)))

(defn ol-map [products-location locations]
  (let [{:keys [center zoom]} @(rf/subscribe [:get [:ui :map-properties]])
        view (ol/View.
              (clj->js
               {:center (or center (coords->js products-location))
                :zoom (or zoom 11)}))
        layers (make-layers locations)
        map-component (ol/Map.
                       (clj->js
                        {:view view
                         :layers layers}))
        singleSelect (SelectInteraction. (clj->js {:style (style {:image (icon (machtfit/static "map/marker-active.png"))})}))]
    (r/create-class
     {:display-name "machtfit-map"

      :component-did-mount
      (fn []
        (println "component-did-mount")

        (-> map-component
            (.setTarget "map"))

        ^js (.addInteraction map-component singleSelect)

        (.on map-component "pointermove"
             (fn [e]
               (let [hit (->> e
                              .-originalEvent
                              ^js (.getEventPixel map-component)
                              ^js (.hasFeatureAtPixel map-component))]
                 (set! (-> map-component
                           ^js (.getTargetElement)
                           .-style
                           .-cursor)
                       (if hit
                         "pointer"
                         "")))))

        (.on map-component "click"
             (fn [e]
               (let [feature ^js (.forEachFeatureAtPixel map-component
                                                         ^js (.-pixel e)
                                                         (fn [feature _layer]
                                                           feature))]
                 (if feature
                   (let [coords (-> feature .-values_ js->clj (get "coords"))]
                     (rf/dispatch [::set-clicked-marker-coords coords])
                     (when (responsive/below-bootstrap-grid-tier? :md)
                       (u/scroll-element-into-view "map"))
                     (rf/dispatch [::product-marker-clicked coords]))
                   (rf/dispatch [::set-clicked-marker-coords nil])))))

        (.on view "change"
             (fn [e]
               (let [new-zoom (->> e
                                   .-originalEvent
                                   ^js (.getZoom view))
                     new-center (->> e
                                     .-originalEvent
                                     ^js (.getCenter view))]
                 (rf/dispatch [::set-map-properties {:zoom new-zoom
                                                     :center new-center}])))))

      :component-did-update
      (fn [this _old-argv]
        (println "component-did-update")

        (let [new-args (rest (r/argv this))
              [products-location locations] new-args]

          ^js (.setCenter view
                          (coords->js products-location))
          ^js (.setLayers map-component
                          (make-layers locations))))

      :component-will-unmount
      (fn []
        (println "about to unmount")
        ^js (.dispose map-component)
        ^js (.dispose view))

      :reagent-render

      (fn [_location _locations]
        (println "reagent-render")
        [:div.product-map-container
         [:div#map.product-map
          {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "map" "canvas")}]

         [no-results-popup]
         [product-list-for-location]])})))
